/**
 * ALDER SEARCH — Insights Hub Page
 * Full listing of all articles with category filtering
 */

import { useEffect, useState } from "react";
import { Link } from "wouter";
import { insights, leadMagnets } from "@/data/insights";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

const categories = ["All", ...Array.from(new Set([...insights, ...leadMagnets].map((i) => i.category)))];

export default function InsightsPage() {
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    const prevTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content") ?? "";
    const insightsTitle = "VC & PE Leadership Insights | Alder Search";
    const insightsDesc = "Practitioner perspectives on product talent, value creation, and leadership in VC- and PE-backed businesses.";

    document.title = insightsTitle;
    if (metaDesc) metaDesc.setAttribute("content", insightsDesc);

    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const twTitle = document.querySelector('meta[name="twitter:title"]');
    const twDesc = document.querySelector('meta[name="twitter:description"]');
    const prevOgTitle = ogTitle?.getAttribute("content") ?? "";
    const prevOgDesc = ogDesc?.getAttribute("content") ?? "";
    const prevOgUrl = ogUrl?.getAttribute("content") ?? "";

    ogTitle?.setAttribute("content", insightsTitle);
    ogDesc?.setAttribute("content", insightsDesc);
    ogUrl?.setAttribute("content", "https://aldersearch.com/insights");
    twTitle?.setAttribute("content", insightsTitle);
    twDesc?.setAttribute("content", insightsDesc);


    // BreadcrumbList JSON-LD
    const breadcrumb = document.createElement("script");
    breadcrumb.type = "application/ld+json";
    breadcrumb.id = "insights-breadcrumb-jsonld";
    breadcrumb.appendChild(document.createTextNode(JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://aldersearch.com/" },
        { "@type": "ListItem", "position": 2, "name": "Insights", "item": "https://aldersearch.com/insights" }
      ]
    })));
    document.head.appendChild(breadcrumb);
    return () => {
      document.title = prevTitle;
      if (metaDesc) metaDesc.setAttribute("content", prevDesc);
      ogTitle?.setAttribute("content", prevOgTitle);
      ogDesc?.setAttribute("content", prevOgDesc);
      ogUrl?.setAttribute("content", prevOgUrl);
      document.getElementById("insights-breadcrumb-jsonld")?.remove();
    };
  }, []);

  const filtered =
    activeCategory === "All"
      ? insights
      : insights.filter((i) => i.category === activeCategory);
  const visibleLeadMagnets =
    activeCategory === "All"
      ? leadMagnets
      : leadMagnets.filter((resource) => resource.category === activeCategory);

  return (
    <div className="min-h-screen" style={{ background: "oklch(0.97 0.008 80)" }}>
      <Navigation />

      {/* Hero */}
      <div
        className="pt-36 pb-20 px-8"
        style={{ background: "oklch(0.22 0.065 155)" }}
      >
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px w-10" style={{ background: "oklch(0.62 0.085 75)" }} />
            <span
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                fontSize: "0.7rem",
                color: "oklch(0.62 0.085 75)",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
              }}
            >
              Perspectives
            </span>
          </div>
          <h1
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 700,
              fontSize: "clamp(2.4rem, 5vw, 4rem)",
              color: "oklch(0.952 0.012 80)",
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              marginBottom: "1.2rem",
            }}
          >
            Insights on VC &amp; PE Leadership
          </h1>
          <p
            style={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontWeight: 300,
              fontSize: "1.05rem",
              lineHeight: 1.8,
              color: "oklch(0.72 0.02 155)",
              maxWidth: "560px",
            }}
          >
            Practitioner perspectives on product talent, value creation, and
            leadership in VC- and PE-backed businesses.
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <div
        className="sticky top-16 z-40 px-8 py-4"
        style={{
          background: "oklch(0.97 0.008 80 / 0.96)",
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid oklch(0.88 0.01 80)",
        }}
      >
        <div className="max-w-5xl mx-auto flex gap-2 flex-wrap">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className="px-4 py-1.5 text-xs font-medium tracking-wide transition-all duration-200"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                letterSpacing: "0.06em",
                borderRadius: "2px",
                border:
                  activeCategory === cat
                    ? "1px solid oklch(0.28 0.07 155)"
                    : "1px solid oklch(0.85 0.01 80)",
                background:
                  activeCategory === cat
                    ? "oklch(0.28 0.07 155)"
                    : "transparent",
                color:
                  activeCategory === cat
                    ? "oklch(0.92 0.01 80)"
                    : "oklch(0.45 0.02 155)",
                cursor: "pointer",
              }}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Articles grid */}
      <div className="px-8 py-16">
        <div className="max-w-5xl mx-auto">
          {visibleLeadMagnets.map((resource) => (
            <Link
              key={resource.slug}
              href={`/insights/${resource.slug}`}
              className="block mb-12 group"
              style={{ textDecoration: "none" }}
            >
              <div
                className="p-8 lg:p-10 transition-all duration-300"
                style={{
                  background: "linear-gradient(135deg, oklch(0.245 0.065 155), oklch(0.19 0.055 155))",
                  borderRadius: "4px",
                  border: "1px solid oklch(0.62 0.085 75 / 0.5)",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.borderColor = "oklch(0.72 0.09 75)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.borderColor = "oklch(0.62 0.085 75 / 0.5)";
                }}
              >
                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <span
                    style={{
                      fontFamily: "'DM Sans', system-ui, sans-serif",
                      fontSize: "0.65rem",
                      color: "oklch(0.78 0.1 75)",
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      background: "oklch(0.62 0.085 75 / 0.16)",
                      padding: "4px 10px",
                      borderRadius: "2px",
                    }}
                  >
                    Free PDF Guide
                  </span>
                  <span
                    style={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: "0.65rem",
                      color: "oklch(0.65 0.02 155)",
                      letterSpacing: "0.08em",
                    }}
                  >
                    {resource.category} · {resource.date}
                  </span>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-7 items-end">
                  <div>
                    <h2
                      className="mb-3 group-hover:opacity-80 transition-opacity"
                      style={{
                        fontFamily: "'Playfair Display', Georgia, serif",
                        fontWeight: 700,
                        fontSize: "clamp(1.65rem, 3.2vw, 2.45rem)",
                        color: "oklch(0.952 0.012 80)",
                        lineHeight: 1.16,
                        letterSpacing: "-0.018em",
                      }}
                    >
                      {resource.title}
                    </h2>
                    <p
                      style={{
                        fontFamily: "'Source Serif 4', Georgia, serif",
                        fontWeight: 300,
                        fontSize: "1rem",
                        lineHeight: 1.75,
                        color: "oklch(0.72 0.02 155)",
                        maxWidth: "720px",
                      }}
                    >
                      {resource.excerpt}
                    </p>
                  </div>
                  <span
                    style={{
                      fontFamily: "'DM Sans', system-ui, sans-serif",
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      color: "oklch(0.82 0.1 75)",
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Get the guide →
                  </span>
                </div>
              </div>
            </Link>
          ))}

          {/* Featured article (first in list if "All") */}
          {activeCategory === "All" && filtered[0] && (
            <Link
              href={`/insights/${filtered[0].slug}`}
              className="block mb-12 group"
              style={{ textDecoration: "none" }}
            >
              <div
                className="p-10 transition-all duration-300"
                style={{
                  background: "oklch(0.245 0.065 155)",
                  borderRadius: "4px",
                  border: "1px solid oklch(0.32 0.07 155)",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.borderColor =
                    "oklch(0.62 0.085 75 / 0.6)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.borderColor =
                    "oklch(0.32 0.07 155)";
                }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span
                    style={{
                      fontFamily: "'DM Sans', system-ui, sans-serif",
                      fontSize: "0.65rem",
                      color: "oklch(0.62 0.085 75)",
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                      background: "oklch(0.62 0.085 75 / 0.12)",
                      padding: "3px 10px",
                      borderRadius: "2px",
                    }}
                  >
                    Featured
                  </span>
                  <span
                    style={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: "0.65rem",
                      color: "oklch(0.55 0.02 155)",
                      letterSpacing: "0.08em",
                    }}
                  >
                    {filtered[0].category}
                  </span>
                </div>
                <h2
                  className="mb-4 group-hover:opacity-80 transition-opacity"
                  style={{
                    fontFamily: "'Playfair Display', Georgia, serif",
                    fontWeight: 700,
                    fontSize: "clamp(1.5rem, 3vw, 2.2rem)",
                    color: "oklch(0.952 0.012 80)",
                    lineHeight: 1.2,
                    letterSpacing: "-0.015em",
                  }}
                >
                  {filtered[0].title}
                </h2>
                <p
                  style={{
                    fontFamily: "'Source Serif 4', Georgia, serif",
                    fontWeight: 300,
                    fontSize: "0.95rem",
                    lineHeight: 1.75,
                    color: "oklch(0.72 0.02 155)",
                    maxWidth: "680px",
                    marginBottom: "1.5rem",
                  }}
                >
                  {filtered[0].excerpt}
                </p>
                <div className="flex items-center gap-4">
                  <span
                    style={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: "0.62rem",
                      color: "oklch(0.55 0.02 155)",
                      letterSpacing: "0.06em",
                    }}
                  >
                    {filtered[0].date}
                  </span>
                  <span
                    style={{
                      fontFamily: "'DM Sans', system-ui, sans-serif",
                      fontSize: "0.72rem",
                      color: "oklch(0.62 0.085 75)",
                      letterSpacing: "0.08em",
                      marginLeft: "auto",
                    }}
                  >
                    Read article →
                  </span>
                </div>
              </div>
            </Link>
          )}

          {/* Remaining articles grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(activeCategory === "All" ? filtered.slice(1) : filtered).map(
              (article) => (
                <Link
                  key={article.slug}
                  href={`/insights/${article.slug}`}
                  className="block group h-full"
                  style={{ textDecoration: "none" }}
                >
                  <div
                    className="p-7 h-full flex flex-col transition-all duration-300"
                    style={{
                      background: "white",
                      borderRadius: "4px",
                      border: "1px solid oklch(0.88 0.01 80)",
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLDivElement).style.borderColor =
                        "oklch(0.28 0.07 155 / 0.4)";
                      (e.currentTarget as HTMLDivElement).style.boxShadow =
                        "0 4px 24px oklch(0.28 0.07 155 / 0.08)";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLDivElement).style.borderColor =
                        "oklch(0.88 0.01 80)";
                      (e.currentTarget as HTMLDivElement).style.boxShadow =
                        "none";
                    }}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <span
                        style={{
                          fontFamily: "'DM Mono', monospace",
                          fontSize: "0.6rem",
                          color: "oklch(0.55 0.04 155)",
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          background: "oklch(0.28 0.07 155 / 0.07)",
                          padding: "2px 8px",
                          borderRadius: "2px",
                        }}
                      >
                        {article.category}
                      </span>
                    </div>
                    <h3
                      className="mb-3 group-hover:opacity-75 transition-opacity flex-grow"
                      style={{
                        fontFamily: "'Playfair Display', Georgia, serif",
                        fontWeight: 600,
                        fontSize: "1.05rem",
                        color: "oklch(0.22 0.06 155)",
                        lineHeight: 1.35,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {article.title}
                    </h3>
                    <p
                      style={{
                        fontFamily: "'Source Serif 4', Georgia, serif",
                        fontWeight: 300,
                        fontSize: "0.82rem",
                        lineHeight: 1.7,
                        color: "oklch(0.45 0.015 80)",
                        marginBottom: "1.2rem",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-auto">
                      <span
                        style={{
                          fontFamily: "'DM Mono', monospace",
                          fontSize: "0.58rem",
                          color: "oklch(0.6 0.015 80)",
                          letterSpacing: "0.06em",
                        }}
                      >
                        {article.date}
                      </span>
                      <span
                        style={{
                          fontFamily: "'DM Sans', system-ui, sans-serif",
                          fontSize: "0.68rem",
                          color: "oklch(0.38 0.07 155)",
                          letterSpacing: "0.06em",
                        }}
                      >
                        Read →
                      </span>
                    </div>
                  </div>
                </Link>
              )
            )}
          </div>

          {filtered.length === 0 && visibleLeadMagnets.length === 0 && (
            <p
              className="text-center py-16"
              style={{
                fontFamily: "'Source Serif 4', Georgia, serif",
                color: "oklch(0.55 0.015 80)",
              }}
            >
              No articles in this category yet.
            </p>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
